'use client';

import React, { useState } from 'react';
import TextMeApp from "@/common/TextMeApp";
import withOptionalAuth from "@/components/withOptionalAuth";
import MainLayout from "@/layouts/MainLayout";
import FeatureLinkCard from '@/components/FeatureLinkCard';
import preview1 from '@/assets/personality_preview_1.jpg';
import preview2 from '@/assets/personality_preview_2.jpg';
import preview3 from '@/assets/personality_preview_3.jpg';
import preview4 from '@/assets/personality_preview_4.jpg';
import Image from 'next/image';
import { NextPageWithLayout } from "../_app.page";
import YourTypeQuiz from "./_components/YourTypeQuiz";
import YourTypeWrapped from "./_components/YourTypeWrapped";
import YourTypeResults from "./_components/YourTypeResults";
import YourTypeLoading from "./_components/YourTypeLoading";
import { calculateProfile, InvestorProfile } from "./constants";
import { fetchPersonalityRecommendations, fetchAiCustomPortfolio, fetchFundDetails } from "@/utils/supabase";
import { useLoginModal } from '@/common/LoginModal';

type PageState = 'welcome' | 'quiz' | 'loading' | 'wrapped' | 'results';

function YourTypeIndexPage() {
  const { requireAuth, isAuthenticated } = useLoginModal();
  const [state, setState] = useState<PageState>('welcome');
  const [profile, setProfile] = useState<InvestorProfile | null>(null);
  const [answers, setAnswers] = useState<Record<number, 'A' | 'B' | 'C' | 'D' | 'E'> | null>(null);
  const [showFlash, setShowFlash] = useState(false);
  const [prefetchData, setPrefetchData] = useState<any>(null);
  const [dataReady, setDataReady] = useState(false);

  const handleStartQuiz = () => {
    setState('quiz');
  };

  const handleQuizComplete = async (quizAnswers: Record<number, 'A' | 'B' | 'C' | 'D' | 'E'>) => {
    setAnswers(quizAnswers);
    const resultProfile = calculateProfile(quizAnswers);
    setProfile(resultProfile);

    // Require login before generating results for unauthenticated users
    if (!isAuthenticated) {
      const authenticated = await requireAuth();
      if (!authenticated) return;
    }

    // Enter loading state — the YourTypeLoading component handles timing
    setState('loading');
    setDataReady(false);

    try {
      // Run recommendation + AI portfolio fetch calls in parallel
      const [recommendations, aiPortfolio] = await Promise.all([
        fetchPersonalityRecommendations({
          answers: quizAnswers,
          personalityType: resultProfile.id,
          riskTolerance: resultProfile.riskTolerance,
          timeHorizon: resultProfile.timeHorizon,
        }).catch(err => {
          console.error('Recommendations fetch failed:', err);
          return null;
        }),
        fetchAiCustomPortfolio(resultProfile, quizAnswers).catch(err => {
          console.error('AI Portfolio fetch failed:', err);
          return null;
        })
      ]);

      // Once recommendations are available, prefetch detailed fund data for the primary match
      const primaryFundId = recommendations?.portfolios?.[0]?.portfolio?.fundId;
      let fundDetailsData: any = null;
      if (primaryFundId) {
        fundDetailsData = await fetchFundDetails(primaryFundId).catch(err => {
          console.error('Fund details prefetch failed:', err);
          return null;
        });
      }

      setPrefetchData({ recommendations, aiPortfolio, fundDetails: fundDetailsData });
      setDataReady(true);
    } catch (error) {
      console.error('Global error in prefetching:', error);
      setDataReady(true);
      setState('results');
    }
  };

  const handleLoadingComplete = () => {
    setShowFlash(true);
    setTimeout(() => {
      setState('wrapped');
      setShowFlash(false);
    }, 750);
  };

  const handleWrappedComplete = () => {
    setState('results');
  };

  const handleReplayWrapped = () => {
    setState('wrapped');
  };

  const handleRestart = () => {
    setProfile(null);
    setAnswers(null);
    setPrefetchData(null);
    setDataReady(false);
    setState('welcome');
  };

  return (
    <div className="py-6">
      <div className="flex flex-col max-w-screen-lg lg:mx-auto">
        <TextMeApp />

        {/* Full-width feature card — now inside the constrained container */}
        {state === 'welcome' && (
          <FeatureLinkCard
            onClick={handleStartQuiz}
            title="🧠 Discover Your Investor Type"
            description="Take a quick quiz to find your investing personality and get portfolio recommendations."
            cta="Start test"
            animateOnMount
            horizontalDesktop={isAuthenticated}
            iconNode={
              <div className="flex justify-center" style={{ perspective: '900px' }}>
                <div
                  className="relative h-[120px] w-[68px] animate-carousel-spin md:h-[140px] md:w-[80px]"
                  style={{ transformStyle: 'preserve-3d' }}
                >
                  {[preview1, preview2, preview3, preview4].map((src, i) => (
                    <div
                      key={i}
                      className="absolute inset-0 animate-carousel-card-fade overflow-hidden rounded-lg border border-white/10 shadow-xl"
                      style={{
                        transform: `rotateY(${i * 90}deg) translateZ(80px)`,
                        animationDelay: `${i * -3}s`,
                      }}
                    >
                      <Image
                        src={src}
                        alt={`Quiz result preview ${i + 1}`}
                        fill
                        className="object-cover"
                        sizes="80px"
                      />
                    </div>
                  ))}
                </div>
              </div>
            }
          />
        )}

        <div className="mt-6">
          {state === 'quiz' && <YourTypeQuiz onComplete={handleQuizComplete} />}
          {state === 'loading' && (
            <YourTypeLoading dataReady={dataReady} onComplete={handleLoadingComplete} />
          )}
          {state === 'wrapped' && profile && (
            <YourTypeWrapped
              profile={profile}
              prefetchData={prefetchData}
              onComplete={handleWrappedComplete}
            />
          )}
          {state === 'results' && profile && (
            <YourTypeResults
              profile={profile}
              answers={answers}
              prefetchData={prefetchData}
              onRestart={handleRestart}
              onReplayWrapped={handleReplayWrapped}
            />
          )}
        </div>
      </div>

      {/* Transition Flash Overlay */}
      {showFlash && (
        <div
          className="fixed left-0 top-0 w-[100vw] h-[100vh] z-[9999] bg-white animate-final-flash pointer-events-none"
          style={{ transform: 'none' }}
        />
      )}
    </div>
  );
}

const YourTypeIndexPageOptionalAuth = withOptionalAuth(YourTypeIndexPage) as NextPageWithLayout;

export default YourTypeIndexPageOptionalAuth;

YourTypeIndexPageOptionalAuth.getLayout = MainLayout;
